// oxlint-disable jsx-no-new-object-as-prop

import { EASE } from "@nnf3d/lib";
import { Link } from "@tanstack/react-router";
import { useStore } from "@tanstack/react-store";
import { AnimatePresence, m } from "motion/react";
import { useMemo } from "react";

import { useGlobal } from "../lib/global-context";
import { useIsMobile } from "../lib/hooks/use-breakpoints";
import { loadStore, routeStore } from "../lib/store";
import { hideTooltip, showTooltip } from "../lib/tooltip";
import type { VoidComponent } from "../lib/types";
import { booleanAttribute, imageUrlBuilder } from "../lib/utils";
import { Icon } from "./icon";
import { PillarCarousel } from "./pillar-carousel";
import { Routes } from "./routes";

const Pane: VoidComponent = () => {
	const { pillars } = useGlobal();
	const isMobile = useIsMobile();
	const { level, params } = useStore(routeStore);
	const isStarted = useStore(loadStore, (state) => state.isStarted);

	const categoryBackTitle = useMemo(() => {
		if (level === "category") {
			return pillars.find((p) => p.slug.current === params.pillar)?.title;
		}

		return null;
	}, [level, params, pillars]);

	const initiativeBackTitle = useMemo(() => {
		if (level === "initiative") {
			return pillars
				.find((p) => p.slug.current === params.pillar)
				?.categories.find((c) => c.slug.current === params.category)?.title;
		}

		return null;
	}, [level, params, pillars]);

	if (isMobile) {
		return (
			<>
				{isStarted && <PillarCarousel />}

				<div
					className="fixed right-0 bottom-0 left-0 flex h-[60dvh] translate-y-full flex-col bg-surface-primary transition-[translate,height] duration-800 ease-[cubic-bezier(.3,.1,0,1)] data-is-initiative:h-[calc(100dvh-5rem)] data-open:translate-y-0 desktop:px-8"
					data-open={booleanAttribute(level !== "index")}
					data-is-initiative={booleanAttribute(level === "initiative")}
					id="menu"
				>
					<Routes />
				</div>
			</>
		);
	}

	const backTitleAnimation = {
		initial: { opacity: 0 },
		animate: { opacity: 1, transition: { duration: 0.5, ease: EASE.OUT_QUART } },
		exit: { opacity: 0, transition: { duration: 0.1, ease: EASE.IN_QUART } },
	} as const satisfies Parameters<typeof m.div>[0];

	const paneAnimation = {
		initial: { x: "-100%" },
		animate: {
			x: "0",
			transition: { duration: 1.2, ease: [0.4, 0.1, 0.2, 1], restDelta: 0.001, delay: 1.2 },
		},
		exit: {
			x: "-100%",
			transition: { type: "spring", visualDuration: 0.8, bounce: 0, restDelta: 0.001 },
		},
	} as const satisfies Parameters<typeof m.div>[0];

	return (
		<AnimatePresence>
			{level !== "index" && (
				<m.div
					{...paneAnimation}
					className="fixed top-0 bottom-0 left-0 flex w-pane-small flex-col bg-surface-primary transition-[width] duration-800 ease-[cubic-bezier(.3,.1,0,1)] data-is-initiative:w-pane-full"
					data-is-initiative={booleanAttribute(level === "initiative")}
					id="menu"
				>
					<nav className="relative flex items-center justify-between px-8 pt-8 pb-2">
						<div className="relative flex gap-0.5">
							<Link
								to="/"
								className="flex h-12 w-18 items-center justify-center border border-transparent bg-white text-foreground hover:bg-accent hover:text-surface-primary active:bg-accent active:text-surface-primary"
							>
								<Icon
									name="home"
									title="Home icon"
									className="size-5 transition-opacity duration-200"
								/>
							</Link>

							<div>
								<AnimatePresence mode="wait">
									{categoryBackTitle && (
										<m.div key="category-back-title" {...backTitleAnimation} className="absolute">
											<Link
												to=".."
												className="fit-content flex h-12 max-w-56 items-center border border-transparent bg-white px-4 text-xs text-foreground hover:bg-accent hover:text-surface-primary active:bg-accent active:text-surface-primary"
											>
												<Icon name="nav-arrow-left" className="size-5 shrink-0" />

												<span className="truncate px-2">{categoryBackTitle}</span>
											</Link>
										</m.div>
									)}

									{initiativeBackTitle && (
										<m.div key="initiative-back-title" {...backTitleAnimation} className="absolute">
											<Link
												to=".."
												className="fit-content flex h-12 max-w-56 items-center border border-transparent bg-white px-4 text-xs text-foreground hover:bg-accent hover:text-surface-primary active:bg-accent active:text-surface-primary"
											>
												<Icon name="nav-arrow-left" className="size-5 shrink-0" />

												<span className="truncate px-2">{initiativeBackTitle}</span>
											</Link>
										</m.div>
									)}
								</AnimatePresence>
							</div>
						</div>

						<div className="flex items-center gap-0.5">
							{pillars.map((pillar) => {
								const isActive = pillar.slug.current === params.pillar;

								return (
									<Link
										key={pillar._key}
										to="/$pillar"
										params={{ pillar: pillar.slug.current }} // oxlint-disable-line jsx-no-new-object-as-prop
										className="flex h-12 w-18 items-center justify-center border border-transparent bg-white hover:bg-accent hover:text-surface-primary data-active:border-accent data-active:text-accent hover:data-active:text-surface-primary"
										data-active={booleanAttribute(isActive)}
										aria-current={isActive ? "page" : undefined}
										onMouseEnter={showTooltip(pillar.title)}
										onMouseLeave={hideTooltip()}
									>
										<span className="flex size-full shrink-0 items-center justify-center">
											<img
												src={imageUrlBuilder(pillar.thumbnail)
													.width(48)
													.auto("format")
													.dpr(Math.min(window.devicePixelRatio, 2))
													.url()}
												alt={pillar.title}
												className="absolute size-12"
											/>
										</span>
									</Link>
								);
							})}
						</div>
					</nav>

					<Routes />
				</m.div>
			)}
		</AnimatePresence>
	);
};

export { Pane };
